import { useEffect, useMemo, useRef, useState } from "react";

interface SelectionState {
    selected: ReadonlySet<string>;
    /** Range-select origin (last plain / ctrl click). */
    anchorId: string | null;
    /** Roving-tabindex focus. */
    focusId: string | null;
    /** Bumped whenever the list should move DOM focus to `focusId`. */
    focusRequest: number;
}

export interface SelectionApi {
    selected: ReadonlySet<string>;
    count: number;
    focusId: string | null;
    focusRequest: number;
    has: (id: string) => boolean;
    /** Select just this item (plain click / arrow keys). */
    selectOnly: (id: string, opts?: { focus?: boolean }) => void;
    /** Ctrl/Cmd-click. */
    toggle: (id: string) => void;
    /** Shift-click / Shift+arrow — range from the anchor; `additive` keeps the rest. */
    extendTo: (id: string, additive?: boolean, opts?: { focus?: boolean }) => void;
    /** Move focus without changing selection (Ctrl+arrow). */
    setFocus: (id: string, opts?: { focus?: boolean }) => void;
    selectAll: () => void;
    clear: () => void;
}

const EMPTY: SelectionState = { selected: new Set(), anchorId: null, focusId: null, focusRequest: 0 };

/**
 * Google-Drive selection model over an ordered id list. Resets whenever
 * `resetKey` changes (folder / page switch) and prunes ids that leave the
 * list, so keyboard actions can never hit items the user can't see.
 */
export function useSelection(ids: string[], resetKey: string): SelectionApi {
    const [state, setState] = useState<SelectionState>(EMPTY);
    const [lastKey, setLastKey] = useState(resetKey);
    if (lastKey !== resetKey) {
        setLastKey(resetKey);
        setState(EMPTY);
    }

    const idsRef = useRef(ids);
    idsRef.current = ids;

    useEffect(() => {
        setState((s) => {
            const valid = new Set(ids);
            let changed = false;
            const next = new Set<string>();
            s.selected.forEach((id) => {
                if (valid.has(id)) next.add(id);
                else changed = true;
            });
            const focusOk = s.focusId === null || valid.has(s.focusId);
            const anchorOk = s.anchorId === null || valid.has(s.anchorId);
            if (!changed && focusOk && anchorOk) return s;
            return {
                ...s,
                selected: next,
                focusId: focusOk ? s.focusId : null,
                anchorId: anchorOk ? s.anchorId : null
            };
        });
    }, [ids]);

    return useMemo<SelectionApi>(() => {
        const bump = (s: SelectionState, focus?: boolean) => (focus ? s.focusRequest + 1 : s.focusRequest);
        return {
            selected: state.selected,
            count: state.selected.size,
            focusId: state.focusId,
            focusRequest: state.focusRequest,
            has: (id) => state.selected.has(id),
            selectOnly: (id, opts) =>
                setState((s) => ({ selected: new Set([id]), anchorId: id, focusId: id, focusRequest: bump(s, opts?.focus) })),
            toggle: (id) =>
                setState((s) => {
                    const next = new Set(s.selected);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return { ...s, selected: next, anchorId: id, focusId: id };
                }),
            extendTo: (id, additive, opts) =>
                setState((s) => {
                    const list = idsRef.current;
                    const anchor = s.anchorId ?? s.focusId ?? id;
                    const a = list.indexOf(anchor);
                    const b = list.indexOf(id);
                    if (a < 0 || b < 0) return { ...s, selected: new Set([id]), anchorId: id, focusId: id, focusRequest: bump(s, opts?.focus) };
                    const [lo, hi] = a < b ? [a, b] : [b, a];
                    const range = list.slice(lo, hi + 1);
                    const next = additive ? new Set([...s.selected, ...range]) : new Set(range);
                    return { ...s, selected: next, anchorId: anchor, focusId: id, focusRequest: bump(s, opts?.focus) };
                }),
            setFocus: (id, opts) => setState((s) => ({ ...s, focusId: id, focusRequest: bump(s, opts?.focus) })),
            selectAll: () => setState((s) => ({ ...s, selected: new Set(idsRef.current) })),
            clear: () => setState((s) => (s.selected.size === 0 ? s : { ...s, selected: new Set(), anchorId: null }))
        };
    }, [state]);
}
